import { usePostHog } from '@posthog/react';
import { useCreateDialog, useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn, useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import Loader from 'components/Loader';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import ReferralModal from 'components/modals/referrals/ReferralModal';
import { useModal } from 'components/modals/useModal';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import SubSection from 'components/section-menu/SubSection';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { useRef, useState } from 'react';
import { Control, FieldValues, FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'store';
import {
    useAddMatterMutation,
    useFindMatterRatesQuery,
    useGetMatterByIdQuery,
    useUpdateMatterMutation,
} from 'store/api/matters';
import { useAddReferralMutation } from 'store/api/referals';
import { MatterAddPrefill, MatterForm } from 'types/matters';
import { errorAlert, successAlert, warningAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { useGoBack, useLeaveForm, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { useRejectedSubmitCapture } from 'utils/useRejectedSubmitCapture';
import { TransferTimeAndExpensesModal } from './TransferTimeAndExpensesModal';
import { requiresStillConfidentialConfirmationOnSave } from './closing/confidentialReasonFormRules';
import { useConfirmCaseStillConfidential } from './closing/useConfirmCaseStillConfidential';
import Tooltip from 'components/@extended/Tooltip';
import CancelButton, { CREATE_DIALOG_ACTION_CLASS } from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import { EntityType } from 'types/entity';
import { OfficeMin } from 'types/offices.ts';
import { ReferralDto } from 'types/referrals';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { AccountingFields } from './form-sections/AccountingFields';
import { ArchivingFields } from './form-sections/ArchivingFields';
import { CaseDetailsFields } from './form-sections/CaseDetailsFields';
import { ClientPartiesFields } from './form-sections/ClientPartiesFields';
import { InvoiceSpecFields } from './form-sections/InvoiceSpecFields';
import { OpponentsFields } from './form-sections/OpponentsFields';
import { OtherFields } from './form-sections/OtherFields';
import { PricingFields } from './form-sections/PricingFields';
import { TeamFields } from './form-sections/TeamFields';
import { EMPTY_MATTER_FORM, MatterFormProps, useMatterForm } from './form-sections/useMatterForm';
import { matterDtoToForm, matterFormToRequest } from './matterFormMapping';
import { useSettings } from 'utils/useSettings';
import { Setting } from 'types/back-office-settings';

type Props = MatterFormProps & {
    refetch?: () => void;
    cancelRedirectPath?: string;
    // On a case started from another, the people priced on the source who are no longer active: the create leaves
    // their rates out.
    inactiveEmployeeIds?: Set<number>;
};

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    // General
    readOnlyCaseNumber: 'matters.general',
    name: 'matters.general',
    clientParty: 'matters.general',
    matterStateType: 'matters.general',
    matterType: 'matters.general',
    reasonOfferLost: 'matters.general',
    outcomeNotes: 'matters.general',
    offerValueType: 'matters.general',
    offerValueLow: 'matters.general',
    offerValueHigh: 'matters.general',
    estimatedEngagementLength: 'matters.general',
    estimatedEngagementUnit: 'matters.general',
    handledByOffice: 'matters.general',
    description: 'matters.general',
    generalNotes: 'matters.general',
    isUsedTimeUsedForRemuneration: 'matters.general',
    isConfidential: 'matters.general',
    confidentialReason: 'matters.general',
    insiderInfo: 'matters.general',
    isInsider: 'matters.general',
    isAccessibleFirmWide: 'matters.general',
    practiceAreas: 'matters.general',
    secondLevelPracticeAreas: 'matters.general',
    dmsPracticeArea: 'matters.general',
    serviceAgreementContract: 'matters.general',
    timecardIncrement: 'matters.general',
    project: 'matters.general',
    openedDate: 'matters.general',
    closedDate: 'matters.general',
    dmsLink: 'matters.general',
    supervisedByEmployee: 'matters.general',
    responsibleEmployee: 'matters.general',
    employees: 'matters.general',
    administrators: 'matters.general',
    primaryContactParty: 'matters.general',
    representedClients: 'matters.general',
    isNoOpponent: 'matters.general',
    opponentParties: 'matters.general',
    targetParties: 'matters.general',
    otherParties: 'matters.general',
    judgeParties: 'matters.general',
    // Pricing and Billing
    pricelistCurrencyType: 'matters.pricing-and-billing',
    pricelists: 'matters.pricing-and-billing',
    isPaymentCurrencyDifferent: 'matters.pricing-and-billing',
    paymentCurrencyType: 'matters.pricing-and-billing',
    pricingLimitationType: 'matters.pricing-and-billing',
    capLimit: 'matters.pricing-and-billing',
    capUnitType: 'matters.pricing-and-billing',
    fixedFee: 'matters.pricing-and-billing',
    preserveNonBillableHours: 'matters.pricing-and-billing',
    recurringFixedFee: 'matters.pricing-and-billing',
    invoicingIntervalType: 'matters.pricing-and-billing',
    discountPct: 'matters.pricing-and-billing',
    discountExpiryDate: 'matters.pricing-and-billing',
    isCostIncluded: 'matters.pricing-and-billing',
    billingNotes: 'matters.pricing-and-billing',
    invoicingInfo: 'matters.pricing-and-billing',
    timecardLanguage: 'matters.pricing-and-billing',
    invoicedByOffice: 'matters.pricing-and-billing',
    primaryRecipientParty: 'matters.pricing-and-billing',
    primaryRecipientAdditionalEmails: 'matters.pricing-and-billing',
    secondaryRecipientParties: 'matters.pricing-and-billing',
    secondaryRecipientAdditionalEmails: 'matters.pricing-and-billing',
    matterClientReference: 'matters.pricing-and-billing',
    invoiceMatterName: 'matters.pricing-and-billing',
    invoiceMatterNameLocalLanguage: 'matters.pricing-and-billing',
    payerParty: 'matters.pricing-and-billing',
    payers: 'matters.pricing-and-billing',
    invoiceTemplate: 'matters.pricing-and-billing',
    invoiceSpecTemplate: 'matters.pricing-and-billing',
    isSpecIncludedInInvoice: 'matters.pricing-and-billing',
    isSpecExcludedFromEmail: 'matters.pricing-and-billing',
    nextInvoicingDate: 'matters.pricing-and-billing',
    paymentNotificationReceivers: 'matters.pricing-and-billing',
    isJointInvoicingDisabled: 'matters.pricing-and-billing',
    matterBlockingPolicyType: 'matters.pricing-and-billing',
    isAmlBlockingExempt: 'matters.pricing-and-billing',
    accountingRevenueType: 'matters.pricing-and-billing',
    // Archiving
    isAutomaticClosingExempt: 'matters.archiving',
    isPaperFile: 'matters.archiving',
    archivedDate: 'matters.archiving',
    archivedInOffice: 'matters.archiving',
    archivingNotes: 'matters.archiving',
    archivingPropertiesType: 'matters.archiving',
    // Other
    isExcludedFromXR: 'matters.other.title',
};

type SubmitOptions = {
    openReferralModal?: boolean;
    openNewCaseAfterSave?: boolean;
};

const MatterPageForm = ({
    type = 'add',
    initialData,
    classifiers,
    refetch,
    currencyAccessible,
    hasConnectedInvoices,
    matter,
    entityCustomFields,
    cancelRedirectPath,
    inactiveEmployeeIds,
}: Props): JSX.Element => {
    useFocusedEditMode('matters');
    const {
        params: { id: matterId, contactId },
        inDialog,
    } = useCreateRoute();
    const form = useMatterForm({
        type,
        initialData,
        classifiers,
        currencyAccessible,
        hasConnectedInvoices,
        matter,
        entityCustomFields,
    });
    const {
        methods,
        mapCustomFieldsToDto,
        isOfferFlowEnabled,
        isChangingToOfferWon,
        hasCreateMatters,
        hasAnyCreateMatterPermission,
        isNewPersonaNonGrataClient,
        customFields,
        isAdministrativeMatter,
        isAccountingRevenueTypeEnabled,
        isSharedPaymentEnabled,
        shownPricingKind,
    } = form;
    const {
        control,
        reset,
        formState: { errors },
    } = methods;
    const handleSubmit = useRejectedSubmitCapture(methods.handleSubmit, type === 'edit' ? 'case-edit' : 'case');
    useCreateDialogForm(methods);
    const draft = useCreateDraft({
        record: 'case',
        methods: { handleSubmit, formState: methods.formState },
        // A draft is checked by the rules a create checks but the required ones.
        toPayload: (formData: MatterForm) =>
            matterFormToRequest(formData, {
                customFields: mapCustomFieldsToDto(formData as unknown as Record<string, unknown>),
                withPayers: isSharedPaymentEnabled,
                inactiveEmployeeIds,
                shownPricingKind,
            }).request,
    });

    const sectionErrors = useSectionErrors(errors, FIELD_TO_SECTION_MAP);
    const navigateWithState = useNavigateWithReturnState();
    const createDialog = useCreateDialog();
    const { openModal, closeModal } = useModal();
    const confirmCaseStillConfidential = useConfirmCaseStillConfidential();
    const hasCreateReferrals = useHasPermissions(['CREATE.REFERRALS']);

    const { buttonsDisabled: stateButtonsDisaled } = useSelector((state) => state.buttons);
    const buttonsDisabled = stateButtonsDisaled || isNewPersonaNonGrataClient;

    const matterSaveDisabledTooltipKey = isNewPersonaNonGrataClient
        ? 'contacts.tooltips.add-new-case-disabled-persona-non-grata'
        : undefined;

    const returnTo =
        cancelRedirectPath || (matterId ? `/matter/${matterId}` : contactId ? `/contact/${contactId}` : '/matters');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();

    const posthog = usePostHog();
    const [updateMatter] = useUpdateMatterMutation();
    const [addMatter] = useAddMatterMutation();
    const [addReferral] = useAddReferralMutation();

    const { classifiers: referralClassifiers, isLoading: isLoadingReferralClassifiers } = useClassifiers({
        codes: ['REFERRAL_TYPE', 'REFERRAL_OTHER_SOURCE_TYPE'],
    });

    const bypassNavigationBlockerRef = useRef(false);

    const onSubmit = async (
        formData: MatterForm,
        action: 'create' | 'update',
        options?: SubmitOptions
    ): Promise<void> => {
        try {
            const { request: data, droppedPeopleNames } = matterFormToRequest(formData, {
                customFields: mapCustomFieldsToDto(formData as unknown as Record<string, unknown>),
                initialForm: action === 'update' ? (initialData ?? EMPTY_MATTER_FORM) : undefined,
                withPayers: isSharedPaymentEnabled,
                inactiveEmployeeIds,
                shownPricingKind,
            });
            if (droppedPeopleNames.length > 0) {
                warningAlert(t('matters.pricing-copy-dropped-people', { names: droppedPeopleNames.join(', ') }));
            }

            if (action === 'create') {
                // A draft is created by completing it: the record keeps the draft's id.
                const created =
                    draft.draftId === null
                        ? addMatter({ ...data }).unwrap()
                        : draft.complete(data).then(({ id }) => ({
                              id,
                              name: data.name,
                              matterStateTypeCode: data.matterStateTypeCode ?? '',
                              originatesFromMatter: data.originatesFromMatterId
                                  ? { id: data.originatesFromMatterId }
                                  : null,
                          }));
                await created
                    .then((resp) => {
                        posthog.capture('matter_created', {
                            is_offer: Boolean(resp.matterStateTypeCode?.startsWith('OFFER')),
                        });
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });

                        leaveForm(`/matter/${resp.id}`);

                        if (options?.openReferralModal) {
                            openModal(
                                <ReferralModal
                                    titleId={'referrals.form.add'}
                                    matterId={resp.id.toString()}
                                    classifiers={referralClassifiers}
                                    onChange={(referralDto: ReferralDto) => {
                                        void (async () => {
                                            try {
                                                await addReferral(referralDto).unwrap();
                                                posthog.capture('referral_added', {
                                                    referral_type: referralDto.referralTypeCode,
                                                    ...(referralDto.otherSourceTypeCode && {
                                                        other_source_type: referralDto.otherSourceTypeCode,
                                                    }),
                                                });
                                                successAlert(t('referrals.referrals-created'));
                                            } catch (e) {
                                                console.error('onSubmit referral error: ', e);
                                                errorAlert(t('forms.error-submitting-the-form'));
                                            }
                                        })();
                                    }}
                                    onClose={closeModal}
                                />,
                                closeModal
                            );
                        }

                        // The link on the response is what confirms there is an offer Case to transfer from.
                        // The step runs once the add form is left, on top of the destination Case's page, of
                        // the page the create dialog was opened over, or of that dialog where it stayed open
                        // for another creation: the form is done and would otherwise still accept a second
                        // submit.
                        if (isOfferFlowEnabled && resp.originatesFromMatter) {
                            openModal(
                                <TransferTimeAndExpensesModal
                                    sourceMatterId={resp.originatesFromMatter.id}
                                    destinationMatter={{
                                        id: resp.id,
                                        name: resp.name,
                                        matterStateTypeCode: resp.matterStateTypeCode,
                                    }}
                                    onClose={closeModal}
                                />,
                                closeModal
                            );
                        }
                    })
                    .catch((e) => console.error(e));
            } else if (action === 'update' && matterId && !isNaN(Number(matterId))) {
                await updateMatter({
                    ...data,
                    id: matterId,
                })
                    .unwrap()
                    .then(() => {
                        bypassNavigationBlockerRef.current = true;
                        if (refetch) refetch();
                        reset({}, { keepValues: true });

                        if (options?.openNewCaseAfterSave) {
                            const prefill: MatterAddPrefill = { originatesFromMatterId: Number(matterId) };
                            if (createDialog) {
                                // The create dialog opens over the page it is asked from, so this saved form
                                // is left first and the new Case is filled in over the Offer Won Case's page.
                                leaveForm(returnTo);
                                createDialog.open('/matter/new', { prefill, returnTo });
                            } else {
                                navigateWithState('/matter/new', { state: { prefill, returnTo }, replace: true });
                            }
                        } else {
                            leaveForm(returnTo);
                        }
                    })
                    .catch((e) => console.error(e));
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitWithInactiveOfficeCheck = (
        formData: MatterForm,
        action: 'create' | 'update',
        options?: SubmitOptions
    ) => {
        const handledByOfficeInactive = formData.handledByOffice?.isActive === false;
        const invoicedByOfficeInactive = (formData.invoicedByOffice as OfficeMin)?.isActive === false;

        if (handledByOfficeInactive || invoicedByOfficeInactive) {
            const inactiveFields: string[] = [];
            if (handledByOfficeInactive) inactiveFields.push(t('matters.handled-by-office'));
            if (invoicedByOfficeInactive) inactiveFields.push(t('matters.invoiced-by-office'));

            openModal(
                <ConfirmationModal
                    titleId="warning"
                    confirmId="matters.inactive-office-warning.proceed"
                    cancelId="cancel"
                    content={
                        <FormattedMessage
                            id="matters.inactive-office-warning.message"
                            values={{ fields: inactiveFields.join('" and "') }}
                        />
                    }
                    onCancel={closeModal}
                    onConfirm={() => {
                        closeModal();
                        onSubmit(formData, action, options);
                    }}
                />,
                closeModal
            );
        } else {
            onSubmit(formData, action, options);
        }
    };

    const submitCreateMatter = () => {
        handleSubmit((data) => submitWithInactiveOfficeCheck(data, 'create'))();
    };

    const submitUpdateWithConfidentialityCheck = async (formData: MatterForm, options?: SubmitOptions) => {
        if (
            requiresStillConfidentialConfirmationOnSave(formData, matter?.matterStateTypeCode) &&
            !(await confirmCaseStillConfidential())
        ) {
            return;
        }
        submitWithInactiveOfficeCheck(formData, 'update', options);
    };

    const submitUpdateMatter = () => {
        handleSubmit((data) => submitUpdateWithConfidentialityCheck(data))();
    };

    const submitCreateMatterAndAddReferral = () => {
        handleSubmit((data) => submitWithInactiveOfficeCheck(data, 'create', { openReferralModal: true }))();
    };

    const submitUpdateMatterAndOpenNewCase = () => {
        handleSubmit((data) => submitUpdateWithConfidentialityCheck(data, { openNewCaseAfterSave: true }))();
    };

    if (form.isLoading || isLoadingReferralClassifiers) {
        return <Loader />;
    }

    // Show inital name if is edit form. Dont do dynamically to prevent unneeded rerenders
    const getFormTitle = () => {
        switch (type) {
            case 'edit':
                return initialData?.name;
            default:
                return t('matters.add-new-matter');
        }
    };

    // Get header buttons based on form type. Currently cannot delete matters
    const getFormHeader = () => {
        const actions: JSX.Element[] = [<CancelButton key="cancel" onClick={goBack} />];

        if (type === 'add') {
            if (draft.button) {
                actions.push(draft.button);
            }
            // A draft being edited is completed by its main action alone, as the draft's own form draws it.
            if (draft.draftId === null) {
                actions.push(
                    <Tooltip key="saveAndAddReferral" text={matterSaveDisabledTooltipKey}>
                        <div>
                            <PermissionButton
                                // The matter is saved before the referral modal opens and that modal has its
                                // own CREATE.REFERRALS gate, so both grants are needed — and PermissionButton
                                // ORs its own checks, hence the single flag.
                                hasAuthority={hasAnyCreateMatterPermission && hasCreateReferrals}
                                color="secondary"
                                // The create dialog keeps the amber fill for its main action.
                                variant={inDialog ? 'outlined' : 'contained'}
                                className={inDialog ? CREATE_DIALOG_ACTION_CLASS : undefined}
                                size="small"
                                onClick={submitCreateMatterAndAddReferral}
                                disabled={buttonsDisabled}
                            >
                                <FormattedMessage
                                    id={
                                        inDialog
                                            ? 'create-dialog.actions.create-and-referral'
                                            : 'matters.save-and-add-referral'
                                    }
                                />
                            </PermissionButton>
                        </div>
                    </Tooltip>
                );
            }
            const save = (
                <SaveButton
                    key="addSave"
                    permissions={['CREATE.MATTERS', 'CREATE.MATTERS.LEADS', 'CREATE.MATTERS.CLIENTS']}
                    onClick={submitCreateMatter}
                    disabled={buttonsDisabled}
                    tooltip={inDialog ? matterSaveDisabledTooltipKey : undefined}
                />
            );
            // The create dialog's footer tells a form's main action by its being a SaveButton, which carries
            // the tooltip itself there. On the page the tooltip stays on the wrapper around it.
            actions.push(
                inDialog ? (
                    save
                ) : (
                    <Tooltip key="addSave" text={matterSaveDisabledTooltipKey}>
                        <div>{save}</div>
                    </Tooltip>
                )
            );
        } else if (type === 'edit' && matterId) {
            if (isChangingToOfferWon) {
                actions.push(
                    <Tooltip key="saveAndOpenNewCase" text={matterSaveDisabledTooltipKey}>
                        <div>
                            <PermissionButton
                                // Saving the status change and opening the resulting Case both have to be
                                // allowed, and PermissionButton ORs its own checks — hence the single flag.
                                hasAuthority={matter?.isEditable === true && hasCreateMatters}
                                color="secondary"
                                variant="contained"
                                size="small"
                                onClick={submitUpdateMatterAndOpenNewCase}
                                disabled={buttonsDisabled}
                            >
                                <FormattedMessage id="matters.save-and-open-new-case" />
                            </PermissionButton>
                        </div>
                    </Tooltip>
                );
            }
            actions.push(
                <Tooltip key="editSave" text={matterSaveDisabledTooltipKey}>
                    <div>
                        <PermissionButton
                            hasAuthority={matter?.isEditable}
                            color="secondary"
                            variant="contained"
                            size="small"
                            onClick={submitUpdateMatter}
                            disabled={buttonsDisabled}
                        >
                            <FormattedMessage id="save" />
                        </PermissionButton>
                    </div>
                </Tooltip>
            );
        }

        return <ActionHeader title={getFormTitle()} actions={actions} onBack={goBack} draft={draft.detail} />;
    };

    return (
        <FormProvider {...methods}>
            <SectionActionsProvider>
                <HighlightedFieldsProvider>
                    <FormWrapper>
                        {getFormHeader()}
                        <DraftSaveMode mode={draft.mode}>
                            <SectionLayout sectionErrors={sectionErrors}>
                                <Section title="matters.general" collapsible>
                                    <CaseDetailsFields form={form} />
                                    <SubSection title="matters.our-team">
                                        <TeamFields form={form} />
                                    </SubSection>
                                    <SubSection title="matters.client-parties">
                                        <ClientPartiesFields form={form} />
                                    </SubSection>
                                    <SubSection title="matters.opponents-targets-and-other">
                                        <OpponentsFields form={form} />
                                    </SubSection>
                                </Section>

                                <Section title="matters.pricing-and-billing" collapsible>
                                    <SubSection title="matters.pricing">
                                        <PricingFields form={form} />
                                    </SubSection>

                                    <SubSection title="matters.invoice-specification">
                                        <InvoiceSpecFields form={form} />
                                    </SubSection>
                                    {isAccountingRevenueTypeEnabled ? (
                                        <SubSection title="matters.accounting-details">
                                            <AccountingFields form={form} />
                                        </SubSection>
                                    ) : null}
                                </Section>

                                <Section title="matters.archiving" collapsible>
                                    <ArchivingFields form={form} />
                                </Section>

                                <Section title="matters.other.title" collapsible>
                                    <OtherFields form={form} />
                                </Section>
                                {customFields?.length > 0 ? (
                                    <CustomFields
                                        control={control as unknown as Control<FieldValues>}
                                        fields={customFields}
                                        allOptional={isAdministrativeMatter}
                                    />
                                ) : null}
                            </SectionLayout>
                        </DraftSaveMode>
                    </FormWrapper>
                </HighlightedFieldsProvider>
            </SectionActionsProvider>
            <ConfirmNavigation control={control} bypassRef={bypassNavigationBlockerRef} draft={draft.leave} />
        </FormProvider>
    );
};

/** The add form loaded with what a draft holds, read as a case's edit form reads it. */
const MatterDraftForm = ({ draftId, ...props }: Props & { draftId: number }) => {
    const isAdministratorFieldEnabled = useSettings().isSettingEnabled(Setting.ADMINISTRATOR_FIELD);
    const [initialData, setInitialData] = useState<MatterForm>();
    const { data: matter } = useGetMatterByIdQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { data: pricelists } = useFindMatterRatesQuery(String(draftId), { refetchOnMountOrArgChange: true });
    const { initialCustomFieldsObject, isLoading: isLoadingCustomFields } = useCustomFields({
        entityType: EntityType.MATTER,
        fields: matter?.customFields,
    });

    // Frozen once loaded, as the edit form's: a refetch must not reset what the user is typing.
    if (!initialData && matter && pricelists && !isLoadingCustomFields && props.classifiers) {
        setInitialData(
            matterDtoToForm(matter, {
                classifiers: props.classifiers,
                pricelists,
                customFieldValues: initialCustomFieldsObject,
                isAdministratorFieldEnabled,
            })
        );
    }

    return initialData ? <MatterPageForm {...props} type="add" initialData={initialData} /> : <Loader />;
};

/** The case form; a create URL with `?draft=<id>` edits that draft instead of a blank case. */
const MatterPageFormOrDraft = (props: Props) => {
    const draftId = draftIdIn(useCreateRoute().location.search);

    return props.type !== 'edit' && draftId !== null ? (
        <MatterDraftForm key={draftId} draftId={draftId} {...props} />
    ) : (
        <MatterPageForm {...props} />
    );
};

export default MatterPageFormOrDraft;
